import api from "api";
import { QueryKey, useMutation, useQueryClient } from "react-query";
import { queryKeys } from "utils/misc";
import { BulkUpdateReferralsParams, ReferralTrackingResponse } from "./types";

interface MutationContext {
  previous: { key: QueryKey; data: ReferralTrackingResponse | undefined }[];
}

const useBulkUpdateReferrals = () => {
  const queryClient = useQueryClient();

  return useMutation<unknown, unknown, BulkUpdateReferralsParams, MutationContext>(
    ({ ids, fields }) =>
      api.call("/uk/referral_tracking/bulk_update", {
        method: "PATCH",
        data: { ids, ...fields },
      }),
    {
      onMutate: async ({ ids, fields }) => {
        await queryClient.cancelQueries(queryKeys.ukReferralTracking());

        const queries = queryClient.getQueriesData<ReferralTrackingResponse>(queryKeys.ukReferralTracking());
        const previous = queries.map(([key, data]) => ({ key, data }));

        const idSet = new Set(ids);
        queryClient.setQueriesData<ReferralTrackingResponse | undefined>(
          queryKeys.ukReferralTracking(),
          (old) => {
            if (!old) return old;
            return {
              ...old,
              items: old.items.map((item) => (idSet.has(item.id) ? { ...item, ...fields } : item)),
            };
          }
        );

        return { previous };
      },
      onError: (_err, _vars, context) => {
        if (context?.previous) {
          context.previous.forEach(({ key, data }) => {
            queryClient.setQueryData(key, data);
          });
        }
      },
    }
  );
};

export default useBulkUpdateReferrals;
